/**
 * Rain skip display helpers. The rules and the decision are the server's
 * (HHNodeServer utils/rainSkip.js, rainSkipService); this only names things.
 */

import type { RainSkipReason, RainSource, SiteRainRule, ValveRainRule } from '../services/espnowIrrigation';

/** Hex only, upper case: the server stores valve rules this way. */
export const macKey = (mac: string | null | undefined): string =>
  String(mac ?? '').toUpperCase().replace(/[^0-9A-F]/g, '');

export const valveKey = (mac: string | null | undefined, relay: number): string =>
  `${macKey(mac)}#${Number(relay)}`;

/** "10 mm in 12 h" */
export function ruleText(rainMm: number, hours: number): string {
  return `${Number(rainMm)} mm in ${hours} h`;
}

/** Where a total came from, for a sentence. */
export function sourceText(source: RainSource | null | undefined, stationName?: string | null): string {
  if (source === 'station') return stationName ? `station ${stationName}` : 'on-farm station';
  if (source === 'open_meteo') return 'Open-Meteo, modelled';
  if (source === 'open_meteo_forecast') return 'Open-Meteo forecast';
  return 'source unknown';
}

/** "14.2 mm in the last 12 h" or "8 mm forecast in the next 24 h". */
export function reasonText(reason: RainSkipReason | undefined, rainMm: number, hours: number): string {
  return reason === 'forecast'
    ? `${rainMm} mm forecast in the next ${hours} h`
    : `${rainMm} mm in the last ${hours} h`;
}

/** What one valve will do, in a phrase. */
export function valveRuleText(site: SiteRainRule, rule: ValveRainRule | undefined): string {
  const mode = rule?.mode ?? 'site';
  if (mode === 'never') return 'Never skipped';
  const parts: string[] = [];
  if (mode === 'custom') {
    if (rule?.rain_mm != null && rule.hours != null) parts.push(`${ruleText(rule.rain_mm, rule.hours)} fell`);
    if (rule?.forecast_mm != null && rule.forecast_hours != null) {
      parts.push(`${rule.forecast_mm} mm forecast in ${rule.forecast_hours} h`);
    }
  } else {
    if (site.enabled) parts.push(`${ruleText(site.rain_mm, site.hours)} fell`);
    if (site.forecast_enabled && site.forecast_mm != null && site.forecast_hours != null) {
      parts.push(`${site.forecast_mm} mm forecast in ${site.forecast_hours} h`);
    }
  }
  return parts.length ? `Skipped if ${parts.join(' or ')}` : 'Waters whatever the rain';
}
